(() => {
  if (customElements.get("m-action-video-popup")) return;

  /* --------------------------------------------------------------------------
     Button background video
     --------------------------------------------------------------------------
     Shopify only lists the full-resolution mp4 in `video.sources`, and a plain
     <video src> makes the browser buffer the whole file (12MB here) even though
     the button is 146x33px and we only ever show a few seconds of it.

     So we do two things before handing anything to the player:
       1. Resolve the smallest rendition. The HLS playlist enumerates the SD/HD
          variants Shopify generated; their progressive mp4 twins follow a fixed
          naming convention, so we probe for one and fall back to the listed mp4.
       2. Fetch only the leading bytes of that file. These mp4s are faststart
          (ftyp -> moov -> mdat), so a byte-truncated copy is still a valid,
          playable clip. We slice roughly CLIP_SECONDS worth and play it from a
          blob, which caps the transfer at a few hundred KB.

     Every step degrades to the previous behaviour on failure, and if the whole
     thing throws the button just keeps its plain white background.
     ------------------------------------------------------------------------ */

  const DEFAULT_CLIP_SECONDS = 3;
  /* Bitrate is not constant, so grab a little more than the clip needs to be
     sure the last frames before the loop point are actually present. */
  const BYTE_MARGIN = 1.25;

  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const isDataSaving = () => {
    const conn = navigator.connection;
    return Boolean(conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || "")));
  };

  /* "<base>.SD-480p.1.2Mbps.hls.<id>_.m3u8" -> "<base>.SD-480p-1.2Mbps-<id>.mp4" */
  const HLS_VARIANT = /^(.+)\.([A-Z]{2}-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_*\.m3u8$/;

  const resolveTotalBytes = async (url) => {
    const probe = await fetch(url, { headers: { Range: "bytes=0-0" } });
    if (!probe.ok) return 0;
    return Number((probe.headers.get("content-range") || "").split("/")[1]) || 0;
  };

  /* Returns the URL of the smallest progressive mp4 we can actually reach. */
  const findLightestMp4 = async (fallbackUrl, hlsUrl) => {
    if (!hlsUrl) return fallbackUrl;
    try {
      const res = await fetch(hlsUrl);
      if (!res.ok) return fallbackUrl;
      const lines = (await res.text()).split("\n");

      const variants = [];
      lines.forEach((line, i) => {
        const res = line.match(/RESOLUTION=\d+x(\d+)/);
        const name = (lines[i + 1] || "").trim();
        if (res && name) variants.push({ height: Number(res[1]), name });
      });
      variants.sort((a, b) => a.height - b.height);

      const [path, query = ""] = hlsUrl.split("?");
      const dir = path.slice(0, path.lastIndexOf("/") + 1);

      for (const variant of variants) {
        const parts = variant.name.match(HLS_VARIANT);
        if (!parts) continue;
        const candidate =
          dir + parts[1] + "." + parts[2] + "-" + parts[3] + "-" + parts[4] + ".mp4" +
          (query ? "?" + query : "");
        if (candidate === fallbackUrl) return fallbackUrl;
        if (await resolveTotalBytes(candidate)) return candidate;
      }
    } catch (e) {
      /* Naming convention changed or the playlist is unreachable. */
    }
    return fallbackUrl;
  };

  /* Downloads just the head of the file and returns a blob URL, or null. */
  const buildClipUrl = async (url, clipSeconds, durationMs) => {
    if (!durationMs) return null;
    const total = await resolveTotalBytes(url);
    if (!total) return null;

    const share = Math.min(1, ((clipSeconds * 1000) / durationMs) * BYTE_MARGIN);
    if (share >= 1) return null; // clip covers the whole video: nothing to save

    const end = Math.ceil(total * share);
    const res = await fetch(url, { headers: { Range: "bytes=0-" + end } });
    if (!res.ok) return null;
    return URL.createObjectURL(new Blob([await res.arrayBuffer()], { type: "video/mp4" }));
  };

  class ActionVideoPopup extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.querySelector("[data-open-action-video]");
      this.overlay = this.querySelector("[data-action-video-overlay]");
      this.dialog = this.querySelector("[data-action-video-dialog]");
      this.closeBtn = this.querySelector("[data-close-action-video]");
      this.video = this.querySelector("[data-action-video-player]");
      this.bgVideo = this.querySelector("[data-action-video-bg]");
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.trigger || !this.overlay) return;
      if (this.overlay.parentNode !== document.body) {
        document.body.appendChild(this.overlay);
      }
      this.trigger.addEventListener("click", this.open.bind(this));
      this.closeBtn && this.closeBtn.addEventListener("click", this.close.bind(this));
      this.overlay.addEventListener("click", (e) => {
        if (!this.dialog.contains(e.target)) this.close();
      });
      this.setupBackgroundVideo();
    }

    disconnectedCallback() {
      this.bgObserver && this.bgObserver.disconnect();
      if (this.clipUrl) URL.revokeObjectURL(this.clipUrl);
    }

    setupBackgroundVideo() {
      const video = this.bgVideo;
      if (!video || !video.dataset.src) return;
      if (prefersReducedMotion() || isDataSaving()) return;

      const clipSeconds = Number(video.dataset.clipSeconds) || DEFAULT_CLIP_SECONDS;

      // Set imperatively: Safari only honours the muted-autoplay exemption when
      // the property is already true when play() is called.
      video.muted = true;
      video.defaultMuted = true;

      // The truncated file has no data past the clip, so restart just before the
      // end rather than relying on native `loop` (which waits for full duration).
      video.addEventListener("timeupdate", () => {
        if (video.currentTime >= clipSeconds) video.currentTime = 0;
      });
      video.addEventListener(
        "playing",
        () => this.trigger.classList.add("is-video-bg"),
        { once: true }
      );

      this.bgObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            video.pause();
            return;
          }
          this.startBackgroundVideo(clipSeconds);
        },
        { rootMargin: "200px" }
      );
      this.bgObserver.observe(this.trigger);
    }

    async startBackgroundVideo(clipSeconds) {
      const video = this.bgVideo;
      if (this.bgStarted) {
        video.play().catch(() => {});
        return;
      }
      this.bgStarted = true;

      const { src, hls, duration } = video.dataset;
      let source = src;
      try {
        source = await findLightestMp4(src, hls);
        this.clipUrl = await buildClipUrl(source, clipSeconds, Number(duration));
      } catch (e) {
        /* Fall through to streaming the file directly. */
      }

      if (this.clipUrl) {
        video.src = this.clipUrl;
      } else {
        // Could not slice it: stream the lightest rendition as-is.
        video.preload = "auto";
        video.loop = true;
        video.src = source;
      }
      video.play().catch(() => {
        /* Autoplay refused: the button keeps its plain white background. */
      });
    }

    open() {
      this.overlay.hidden = false;
      document.body.classList.add("m-action-video-popup--open");
      requestAnimationFrame(() => this.overlay.classList.add("is-open"));
      this.trigger.setAttribute("aria-expanded", "true");
      document.addEventListener("keydown", this.onKeydown);
      this.bgVideo && this.bgVideo.pause();
      if (this.video) {
        this.video.currentTime = 0;
        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            // Autoplay with sound may be blocked; leave the controls visible so the user can press play.
          });
        }
      }
    }

    close() {
      this.overlay.classList.remove("is-open");
      this.trigger.setAttribute("aria-expanded", "false");
      document.removeEventListener("keydown", this.onKeydown);
      if (this.video) {
        this.video.pause();
      }
      if (this.bgVideo && this.bgVideo.src) {
        this.bgVideo.play().catch(() => {});
      }
      setTimeout(() => {
        this.overlay.hidden = true;
        document.body.classList.remove("m-action-video-popup--open");
      }, 250);
    }

    onKeydown(e) {
      if (e.key === "Escape") this.close();
    }
  }

  customElements.define("m-action-video-popup", ActionVideoPopup);
})();
